import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Switch,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import { useAuthContext } from "../context/AuthContext";

const digitsOnly = (s) => String(s ?? "").replace(/[^\d]/g, "");
const n = (v) => (v === "" || v == null ? null : Number(v));

const AUTH_TYPES = [
  { label: "API Key", value: "API_KEY" },
  { label: "OAuth2", value: "OAUTH2" },
  { label: "Basic", value: "BASIC" },
  { label: "HMAC", value: "HMAC" },
];

const AddIntegrationForm = ({ data, onClose, onSubmit, isLoading, onTest }) => {
  const { userData } = useAuthContext();
  const [errorMessage, setErrorMessage] = useState(null);

  const [form, setForm] = useState({
    ...data,
    status: data?.status == null ? 1 : Number(data.status),
    ordem: data?.ordem ?? "",
    id_usuario: data?.id_usuario || userData?.id,
    id_usuario_ultima_alteracao: userData?.id,

    nome: data?.nome || "",
    provedor: data?.provedor || "",
    tipo_auth: data?.tipo_auth || "API_KEY",
    base_url: data?.base_url || "",
    scopes: Array.isArray(data?.scopes) ? data.scopes.join(",") : (data?.scopes || ""),
    observacoes: data?.observacoes || "",

    // credenciais (obj no payload)
    api_key: "",
    client_id: data?.credentials?.client_id || "",
    client_secret: "",
    username: data?.credentials?.username || "",
    password: "",
    webhook_secret: "",
    webhook_url: data?.webhook_url || "",

    // flags locais para saber se já existe segredo configurado (quando backend ainda não expõe has_*)
    has_api_key: !!data?.credentials?.api_key,
    has_client_secret: !!data?.credentials?.client_secret,
    has_password: !!data?.credentials?.password,
    has_webhook_secret: !!data?.credentials?.webhook_secret,
  });

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      ...data,
      status: data?.status == null ? 1 : Number(data.status),
      ordem: data?.ordem ?? "",
      id_usuario: data?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,

      nome: data?.nome || "",
      provedor: data?.provedor || "",
      tipo_auth: data?.tipo_auth || "API_KEY",
      base_url: data?.base_url || "",
      scopes: Array.isArray(data?.scopes) ? data.scopes.join(",") : (data?.scopes || ""),
      observacoes: data?.observacoes || "",

      api_key: "",
      client_id: data?.credentials?.client_id || "",
      client_secret: "",
      username: data?.credentials?.username || "",
      password: "",
      webhook_secret: "",
      webhook_url: data?.webhook_url || "",

      has_api_key: !!data?.credentials?.api_key,
      has_client_secret: !!data?.credentials?.client_secret,
      has_password: !!data?.credentials?.password,
      has_webhook_secret: !!data?.credentials?.webhook_secret,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const maskSecretField = (field) => {
    setForm((prev) => ({
      ...prev,
      [field]: "",
      [`has_${field}`]: false,
    }));
  };

  const buildPayload = () => {
    const credentials = {
      api_key: form.api_key?.trim() || undefined,
      client_id: form.client_id?.trim() || undefined,
      client_secret: form.client_secret?.trim() || undefined,
      username: form.username?.trim() || undefined,
      password: form.password?.trim() || undefined,
      webhook_secret: form.webhook_secret?.trim() || undefined,
    };

    // remove undefined
    Object.keys(credentials).forEach((k) => credentials[k] === undefined && delete credentials[k]);

    return {
      id: form.id,
      nome: (form.nome || "").trim(),
      provedor: (form.provedor || "").trim(),
      tipo_auth: form.tipo_auth,
      base_url: (form.base_url || "").trim(),
      scopes: (form.scopes || "").split(",").map((s) => s.trim()).filter(Boolean),
      observacoes: form.observacoes || "",
      ordem: n(digitsOnly(form.ordem)),
      status: Number(form.status) === 1 ? 1 : 0,
      id_usuario: form.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
      credentials,
      webhook_url: (form.webhook_url || "").trim() || undefined,
    };
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    try {
      const payload = buildPayload();
      if (!payload.nome) throw new Error("Nome é obrigatório.");
      await onSubmit(payload);
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.message || "Erro ao salvar a integração.");
    }
  };

  const testNow = async () => {
    setErrorMessage(null);
    try {
      const payload = buildPayload();
      await onTest?.(payload.id ? { id: payload.id } : payload);
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.message || "Falha ao testar a integração.");
    }
  };

  const showApiKey = form.tipo_auth === "API_KEY" || form.tipo_auth === "HMAC";
  const showOAuth = form.tipo_auth === "OAUTH2";
  const showBasic = form.tipo_auth === "BASIC";

  return (
    <Paper elevation={3} sx={{ padding: 3, width: "95%", margin: "0 auto" }}>
      <Typography variant="h6" gutterBottom>
        {form?.id ? "Editar Integração" : "Adicionar Integração"}
      </Typography>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <form onSubmit={submit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField label="Nome" name="nome" value={form.nome} onChange={handleChange} fullWidth required />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Tipo de Autenticação"
              name="tipo_auth"
              value={form.tipo_auth}
              onChange={handleChange}
              fullWidth
            >
              {AUTH_TYPES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField label="Provedor" name="provedor" value={form.provedor} onChange={handleChange} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Base URL" name="base_url" value={form.base_url} onChange={handleChange} fullWidth />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Ordem"
              name="ordem"
              value={form.ordem ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, ordem: digitsOnly(e.target.value) }))}
              inputMode="numeric"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <FormControlLabel
              control={
                <Switch
                  checked={Number(form.status) === 1}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.checked ? 1 : 0 }))}
                />
              }
              label={Number(form.status) === 1 ? "Ativa" : "Inativa"}
            />
          </Grid>

          {/* Credenciais dinâmicas */}
          {showApiKey && (
            <Grid item xs={12} md={6}>
              {(() => {
                const hasStored = form.has_api_key;
                const isMasked = hasStored && !form.api_key;
                return (
              <TextField
                label="API Key"
                name="api_key"
                    value={isMasked ? "••••••••" : form.api_key}
                    onChange={isMasked ? undefined : handleChange}
                    type="password"
                    autoComplete="new-password"
                fullWidth
                helperText={
                      isMasked
                        ? "Valor já configurado. Clique para alterar."
                        : !form.api_key && form.has_api_key
                    ? "Já existe uma API Key configurada. Preencha apenas se quiser trocar."
                    : undefined
                }
                    InputProps={isMasked ? { readOnly: true } : undefined}
                    onClick={isMasked ? () => maskSecretField("api_key") : undefined}
                    onFocus={isMasked ? () => maskSecretField("api_key") : undefined}
              />
                );
              })()}
            </Grid>
          )}
          {showOAuth && (
            <>
              <Grid item xs={12} md={6}>
                <TextField label="Client ID" name="client_id" value={form.client_id} onChange={handleChange} fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                {(() => {
                  const hasStored = form.has_client_secret;
                  const isMasked = hasStored && !form.client_secret;
                  return (
                <TextField
                  label="Client Secret"
                  name="client_secret"
                      value={isMasked ? "••••••••" : form.client_secret}
                      onChange={isMasked ? undefined : handleChange}
                      type="password"
                      autoComplete="new-password"
                  fullWidth
                  helperText={
                        isMasked
                          ? "Valor já configurado. Clique para alterar."
                          : !form.client_secret && form.has_client_secret
                      ? "Já existe um Client Secret configurado. Preencha apenas se quiser trocar."
                      : undefined
                  }
                      InputProps={isMasked ? { readOnly: true } : undefined}
                      onClick={isMasked ? () => maskSecretField("client_secret") : undefined}
                      onFocus={isMasked ? () => maskSecretField("client_secret") : undefined}
                />
                  );
                })()}
              </Grid>
            </>
          )}
          {showBasic && (
            <>
              <Grid item xs={12} md={6}>
                <TextField label="Username" name="username" value={form.username} onChange={handleChange} fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                {(() => {
                  const hasStored = form.has_password;
                  const isMasked = hasStored && !form.password;
                  return (
                <TextField
                  label="Password"
                  name="password"
                      value={isMasked ? "••••••••" : form.password}
                      onChange={isMasked ? undefined : handleChange}
                      type="password"
                      autoComplete="new-password"
                  fullWidth
                  helperText={
                        isMasked
                          ? "Valor já configurado. Clique para alterar."
                          : !form.password && form.has_password
                      ? "Já existe uma senha configurada. Preencha apenas se quiser trocar."
                      : undefined
                  }
                      InputProps={isMasked ? { readOnly: true } : undefined}
                      onClick={isMasked ? () => maskSecretField("password") : undefined}
                      onFocus={isMasked ? () => maskSecretField("password") : undefined}
                />
                  );
                })()}
              </Grid>
            </>
          )}

          {/* Webhook */}
          <Grid item xs={12} md={8}>
            <TextField
              label="Webhook URL (callback)"
              name="webhook_url"
              value={form.webhook_url}
              onChange={handleChange}
              fullWidth
              placeholder="https://dominio.xpro.me/integrations/webhook/{id}"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            {(() => {
              const hasStored = form.has_webhook_secret;
              const isMasked = hasStored && !form.webhook_secret;
              return (
            <TextField
              label="Webhook Secret"
              name="webhook_secret"
                value={isMasked ? "••••••••" : form.webhook_secret}
                onChange={isMasked ? undefined : handleChange}
                type="password"
                autoComplete="new-password"
              fullWidth
              helperText={
                  isMasked
                    ? "Valor já configurado. Clique para alterar."
                    : !form.webhook_secret && form.has_webhook_secret
                  ? "Já existe um Webhook Secret configurado. Preencha apenas se quiser trocar."
                  : undefined
              }
                InputProps={isMasked ? { readOnly: true } : undefined}
                onClick={isMasked ? () => maskSecretField("webhook_secret") : undefined}
                onFocus={isMasked ? () => maskSecretField("webhook_secret") : undefined}
            />
              );
            })()}
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Scopes (separados por vírgula)"
              name="scopes"
              value={form.scopes}
              onChange={handleChange}
              fullWidth
              placeholder="read,write,offlinescope"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Observações"
              name="observacoes"
              value={form.observacoes}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>Cancelar</Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={testNow} disabled={isLoading}>
              {isLoading ? <CircularProgress size={20} color="inherit" /> : "Testar conexão"}
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {isLoading ? <CircularProgress size={20} color="inherit" /> : "Salvar"}
            </Button>
          </Box>
        </Box>
      </form>
    </Paper>
  );
};

export default AddIntegrationForm;
